import React, { useState, useEffect } from 'react';
import Step from '@components/common/steps/step';
import { Flex } from '@ui-library/misc';
import { LabelDiv } from '@ui-library/typography';
import useStores from '@stores/useStores';
import { isValidInput } from '@components/common/util/util';
import { REGEX_STRING } from '@components/common/constants';
import CodeEditor from '@components/common/codeEditor';
import Button from '@ui-library/button';
import { IModelConfig } from '@stores/model';
import TextInputField from '@ui-library/textInput/textInput';
import { useHistory } from 'react-router-dom';
import { sortBy } from 'lodash';
import classes from '../../../common.module.scss';
import { CreateModelProps } from '../..';
import Preview, { PreviewRespProps } from './preview';
import RouteLeavingGuard from './routeLeavingGuard';
import {
  retrieveErrorMessageOrResponseData,
  getApiErrorMessageDetails,
} from '@components/sources/visualizer/mappingScreens/sourceUtil';

interface ConfigurationProps {
  settings: Partial<CreateModelProps>;
  setNextEnabled?: (enableNext: boolean) => void;
  updateConfig?: (config: Partial<IModelConfig>) => void;
  isViewMode?: boolean;
  insideModal?: boolean;
  sqlModelId?: string;
  isCreation?: boolean;
}

export enum fetchingPreviewStates {
  success = 'success',
  loading = 'loading',
  failed = 'failed',
  notStarted = 'notStarted',
}
const Configuration: React.FC<ConfigurationProps> = ({
  settings,
  isViewMode,
  sqlModelId,
  isCreation,
  insideModal,
  setNextEnabled,
  updateConfig,
}: ConfigurationProps) => {
  const isDescriptionValid =
    !settings.config?.description?.length ||
    isValidInput(REGEX_STRING.MODEL_DESCRIPTION, settings.config?.description);
  const [oldConfig, setOldConfig] = useState(settings.config);
  const [previewResp, setpreviewResp] = useState<PreviewRespProps>();
  const [fetchingPreviewStatus, setFetchingPreviewStatus] = useState(
    fetchingPreviewStates.notStarted,
  );
  const history = useHistory();
  const handleDescriptionChange = (_: unknown, value: string) => {
    if (updateConfig) {
      updateConfig({ ...settings.config, description: value });
    }
  };

  useEffect(() => {
    if (setNextEnabled) {
      setNextEnabled(
        Boolean(
          isDescriptionValid &&
            (isCreation || oldConfig?.sql !== settings.config?.sql
              ? Boolean(
                  previewResp?.rows &&
                    fetchingPreviewStatus === fetchingPreviewStates.success &&
                    !previewResp?.error,
                )
              : true) &&
            settings.config?.sql,
        ),
      );
    }
  }, [
    previewResp,
    isDescriptionValid,
    fetchingPreviewStatus,
    oldConfig?.sql !== settings.config?.sql,
  ]);

  useEffect(() => {
    setpreviewResp({});
    setOldConfig(settings.config);
  }, [isViewMode]);

  const { userStore, sourceDataStore, messagesStore } = useStores();

  const fetchPreview = async () => {
    try {
      setFetchingPreviewStatus(fetchingPreviewStates.loading);
      setpreviewResp({});
      if (!settings.accountId || !settings.config?.sql || !settings.sourceDefinition?.name) {
        return;
      }

      const resp = await sourceDataStore.getPreview(
        settings.accountId,
        settings.sourceDefinition?.name,
        { sql: settings.config.sql, sqlModelId },
        50,
        true,
      );
      const previewOrError = retrieveErrorMessageOrResponseData(resp);
      if ('errorMessage' in previewOrError) {
        setFetchingPreviewStatus(fetchingPreviewStates.failed);
        setpreviewResp({ error: previewOrError.errorMessage });
      } else {
        setFetchingPreviewStatus(fetchingPreviewStates.success);
        setpreviewResp(previewOrError);
      }
    } catch (err) {
      setFetchingPreviewStatus(fetchingPreviewStates.failed);
      messagesStore.showErrorMessage(
        getApiErrorMessageDetails(err, 'Could not fetch query preview').messageWithFallback,
      );
    }
  };

  return (
    <div className={`${classes.configuration__container} ${isViewMode ? classes.disabled : ''}`}>
      <Flex flexDirection="row" className="p-b-md">
        <TextInputField
          onChange={handleDescriptionChange}
          field={{
            value: 'description',
            default: settings.config?.description,
            label: 'Description',
            regex: REGEX_STRING.MODEL_DESCRIPTION,
            regexErrorMessage: 'Invalid model description',
            placeholder: 'Enter the model description',
            readOnly: isViewMode,
          }}
          type="input"
          disabled={!userStore!.isAdmin || isViewMode}
        />
      </Flex>

      <Flex className="text-input__container m-t-lg m-b-sm" spaceBetween>
        <LabelDiv>
          Query
          <span className={classes.required}>*</span>
        </LabelDiv>
      </Flex>
      <RouteLeavingGuard
        shouldBlockNavigation={() => true}
        active={
          !isViewMode &&
          !isCreation &&
          (oldConfig?.sql !== settings.config?.sql ||
            oldConfig?.description !== settings.config?.description)
        }
        navigate={(path: string) => {
          history.push(path);
        }}
      />
      <CodeEditor
        code={settings.config?.sql || ''}
        onChange={(code: string) => {
          if (updateConfig) {
            updateConfig({ ...settings.config, sql: code });
          }
          setFetchingPreviewStatus(fetchingPreviewStates.notStarted);
        }}
        language="sql"
        readOnly={
          !userStore.isAdmin ||
          isViewMode ||
          fetchingPreviewStatus === fetchingPreviewStates.loading
        }
        disableScrolling={insideModal}
      />

      {!isViewMode && (
        <>
          <Button
            secondary
            className="m-v-md"
            name="Run Query"
            disabled={
              !settings.config?.sql || fetchingPreviewStatus === fetchingPreviewStates.loading
            }
            loading={fetchingPreviewStatus === fetchingPreviewStates.loading}
            onClick={fetchPreview}
            data-testid="runQuery"
          />
          <Preview
            rows={previewResp?.rows}
            error={previewResp?.error}
            fetchingPreviewStatus={fetchingPreviewStatus}
            columns={previewResp?.columns && sortBy(previewResp.columns, 'name')}
          />
        </>
      )}
    </div>
  );
};

export { Configuration };

export default (props: ConfigurationProps) => (
  <Step title="Configure Data">
    <div className={classes.add_config}>
      <Configuration {...props} />
    </div>
  </Step>
);
